import React, { useEffect, useState } from 'react';
import { Divider, InputAdornment, Menu, MenuItem } from '@material-ui/core';
import cx from 'classnames';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import { IconSearch } from 'src/assets/icons/Search';
import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomButton } from 'src/components/CustomButton';
import { CustomTextField } from 'src/components/CustomTextField';
import { Loader } from 'src/components/Loader';
import { CHANNEL } from 'src/data/channel';
import { EXTERNAL_CONNECTION_OBJECT_SEARCH } from 'src/data/performance';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { EmptyState } from '../EmptyState';

import { Item } from './components/Item';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const DEBOUNCE_DELAY = 3000;

export const ExternalChannelMenu = ({
  className,
  onSearch,
  searchResults,
  onUpdateSearchLoading,
  searchLoading,
  onResultClick,
  editor,
  onCloseMenu,
}) => {
  const styles = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState(EXTERNAL_CONNECTION_OBJECT_SEARCH.all);
  const [isResultsVisible, setIsResultsVisible] = useState(false);

  const refreshSearch = useDebouncedCallback(() => {
    onSearch({
      channel: CHANNEL.salesforceCrm,
      searchTerm,
      filter,
    });
  }, DEBOUNCE_DELAY);

  const {
    handleMenuClick,
    handleUpdateSearchTerm,
    handleUpdateFilter,
    handleResultClick,
    handleMenuClose,
  } = useHandlers({
    setAnchorEl,
    setSearchTerm,
    setFilter,
    onResultClick,
    editor,
    onCloseMenu,
  });

  useEffect(() => {
    if (!trim(searchTerm)) {
      setIsResultsVisible(false);
      onUpdateSearchLoading(false);
      return;
    }
    onUpdateSearchLoading(true);
    refreshSearch();
    setIsResultsVisible(true);
  }, [searchTerm, filter, onUpdateSearchLoading, refreshSearch]);

  const showResults = !searchLoading && isResultsVisible && searchResults?.length > 0;
  const showEmptyState = !searchLoading && isResultsVisible && searchResults?.length === 0;

  return (
    <div className={cx(className, styles.root)}>
      <CustomTextField
        className={styles.input}
        placeholder="Search"
        variant="outlined"
        value={searchTerm}
        onChange={handleUpdateSearchTerm}
        InputProps={{
          classes: {
            adornedStart: styles.inputStartAdornment,
          },
          startAdornment: (
            <>
              <CustomButton
                className={styles.button}
                onClick={handleMenuClick}
                endIcon={
                  <ChevronIcon
                    className={styles.chevronIcon}
                    direction={anchorEl ? ROTATE_DIRECTION.up : ROTATE_DIRECTION.down}
                  />
                }
              >
                {filter}
              </CustomButton>
              <Divider
                className={styles.divider}
                orientation="vertical"
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onChange={handleUpdateFilter}
              >
                {Object.values(EXTERNAL_CONNECTION_OBJECT_SEARCH).map((option) => (
                  <MenuItem
                    className={styles.menuItem}
                    key={option}
                    value={option}
                    onClick={() => handleUpdateFilter(option)}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
              <InputAdornment position="start">
                <IconSearch className={styles.iconSearch} />
              </InputAdornment>
            </>
          ),
        }}
      />
      <div className={styles.body}>
        {searchLoading && <Loader className={styles.loader} />}
        {showResults &&
          searchResults.map((item, index) => (
            <Item
              key={index}
              item={item}
              onClick={handleResultClick}
            />
          ))}
        {showEmptyState && <EmptyState value={searchTerm} />}
      </div>
    </div>
  );
};

ExternalChannelMenu.propTypes = {
  className: PropTypes.string,
  onSearch: PropTypes.func,
  searchResults: PropTypes.arrayOf(PropTypes.shape({})),
  onUpdateSearchLoading: PropTypes.func,
  searchLoading: PropTypes.bool,
  onResultClick: PropTypes.func,
  editor: PropTypes.shape().isRequired,
  onCloseMenu: PropTypes.func.isRequired,
};

ExternalChannelMenu.defaultProps = {
  className: null,
  onSearch: null,
  searchResults: [],
  onUpdateSearchLoading: null,
  searchLoading: false,
  onResultClick: null,
};
